import { BrowserRouter, Navigate, Route, Routes } from "react-router";

import { Layout } from "@/components/layout";
import { SignIn } from "@/components/sign-in";
import { AuthProvider, useAuth } from "@/context/auth-context";
import { MessagesRoute } from "@/routes/messages";
import { OrdersRoute } from "@/routes/orders";
import { PaymentsRoute } from "@/routes/payments";
import { ShipmentsRoute } from "@/routes/shipments";
import { NewProductRoute } from "@/routes/product-new";
import { ProductsRoute } from "@/routes/products";
import { StockRoute } from "@/routes/stock";

/**
 * The whole app is behind one gate.
 *
 * There is no public surface here — no landing page, no marketing route — so
 * the gate wraps the router rather than guarding individual routes. A screen
 * that has to remember to protect itself eventually forgets, which is the same
 * reasoning that puts `requireRole("ADMIN")` on the router in `arrow-be` rather
 * than on each handler.
 */
function Gate() {
  const { status } = useAuth();

  /**
   * The cold-load window while the refresh cookie is exchanged. Rendering the
   * sign-in form here would flash one at every returning operator on every
   * reload — they are signed in, the browser just cannot prove it yet.
   */
  if (status === "loading") {
    return (
      <div className="flex min-h-full items-center justify-center">
        <p className="text-[13.5px] text-muted">Loading…</p>
      </div>
    );
  }

  if (status !== "authorised") return <SignIn />;

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/orders" element={<OrdersRoute />} />
        <Route path="/payments" element={<PaymentsRoute />} />
        <Route path="/shipments" element={<ShipmentsRoute />} />
        <Route path="/products" element={<ProductsRoute />} />
        {/* Above no other route, but declared after `/products` so the list is
            the one a bare `/products` reaches. */}
        <Route path="/products/new" element={<NewProductRoute />} />
        <Route path="/stock" element={<StockRoute />} />
        <Route path="/messages" element={<MessagesRoute />} />
        {/* Orders is the landing screen: it is what an operator opens the
            console to look at. Everything else is a task they navigate to. */}
        <Route path="*" element={<Navigate to="/orders" replace />} />
      </Route>
    </Routes>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Gate />
      </AuthProvider>
    </BrowserRouter>
  );
}
